/**
 * المنصّة — **الموضع الوحيد الذي يلمس المتصفّح في كل التطبيق**.
 *
 * قاعدة lint تكسر البناء عند ذكر `window` أو `document` أو `localStorage` أو
 * `navigator` في أي ملف آخر. والسبب ليس النظافة: تطبيقٌ أصليّ سيأتي يوماً،
 * وحين يأتي لا نريد البحث عن مئة موضع لمس المتصفّح مباشرةً — نريد ملفاً
 * واحداً يُستبدَل. وهذا الملف هو ذلك الملف.
 *
 * وكل قراءة هنا **تحتمل الغياب**: التخزين المحلي يرمي في وضع التصفّح الخاص،
 * و`matchMedia` غير موجود في بيئة الاختبار. فلا يسقط الإقلاع بسبب أيٍّ منها.
 */

export type ColorSchemePreference = "light" | "dark";

/**
 * **عرضُ التثبيت** — حدثٌ غيرُ قياسيّ تعرفه المتصفّحاتُ الكرومية وحدها.
 *
 * ويُعلَن نوعُه هنا، في الملفّ الوحيد الذي يلمس المتصفّح — فلا تعرف عنه شاشةٌ
 * شيئاً، ولا يُكتَب `any` في شاشةٍ لتسكت الأنواع.
 */
type InstallPromptEvent = Event & { readonly prompt: () => Promise<unknown> };

/** المحفوظُ من آخر عرضٍ — يُستهلَك مرّةً، فالمتصفّحُ لا يُعيده */
let installPrompt: InstallPromptEvent | null = null;

let faultEndpoint: string | null = null;

export const platform = {
  /** هل نحن داخل متصفّح أصلاً؟ الاختبارات والتصيير المسبق قد لا يكونان كذلك */
  get available(): boolean {
    return typeof globalThis.document !== "undefined";
  },

  get documentElement(): HTMLElement | null {
    return typeof globalThis.document === "undefined" ? null : globalThis.document.documentElement;
  },

  get fetch(): typeof globalThis.fetch {
    return globalThis.fetch.bind(globalThis);
  },

  /** معرّف فريد للربط — من المتصفّح حين يوفّره، وإلا فبديلٌ لا يسقط */
  /**
   * رفعُ ملفٍ إلى رابطٍ موقّع — **خارج عميل الـAPI عن قصد**.
   *
   * فهذا الطلب لا يذهب إلى سيرفرنا ولا يحمل جلسةً ولا ردّاً بمغلَّفنا: إذنه في
   * الرابط نفسه. وخلطُه بعميل الـAPI يعني إرسال ترويسة الجلسة إلى طرفٍ آخر.
   */
  async upload(url: string, file: File): Promise<{ ok: boolean; status: number }> {
    const response = await this.fetch(url, {
      method: "PUT",
      body: file,
      /**
       * **ونوعٌ لا يعرفه المتصفّح يُرسَل بايتاتٍ عامة** — وكانت خمسُ نسخٍ من هذا
       * الطلب في خمس شاشات، **ثلاثٌ بهذا البديل واثنتان بدونه**: فملفٌّ بلا نوعٍ
       * يُرفَع من التقرير ويُرسَل بترويسةٍ فارغة من الكارت.
       */
      headers: { "content-type": file.type || "application/octet-stream" },
    });
    return { ok: response.ok, status: response.status };
  },

  /**
   * **ينزّل نصّاً كملفٍّ باسمه** — ولا شاشةٌ تنادي `Blob` ولا `URL` بنفسها.
   *
   * وبلا هذه الدالّة كانت شاشةُ التحليلات تقول «**الملفُّ اتجهّز**» ولا ينزل
   * ملفٌّ — **رسالةٌ تكذب على من قرأها**، وهو ما يمنعه المبدأُ المركزيّ تماماً
   * كما يمنع الخطأَ الصامت. ووصفُ القدرة نفسُه يقول «تنزيل... كملف».
   *
   * ويُفرَّغ الرابطُ بعد النقر — **فمرجعُ ذاكرةٍ باقٍ لكل تصديرٍ يتراكم**.
   */
  downloadText(name: string, text: string, type = "text/csv;charset=utf-8"): boolean {
    if (!this.available) return false;
    const url = globalThis.URL.createObjectURL(new globalThis.Blob([text], { type }));
    const link = globalThis.document.createElement("a");
    link.href = url;
    link.download = name;
    link.rel = "noopener";
    globalThis.document.body.append(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
    return true;
  },

  /** يفتح رابطاً موقّتاً في تبويب جديد — `noopener` فلا يصل الفاتحُ إلى صفحتنا */
  openUrl(url: string): void {
    if (!this.available) return;
    globalThis.window.open(url, "_blank", "noopener,noreferrer");
  },

  /**
   * تأكيدٌ عكوس — **للأفعال التي يمكن التراجع عنها وحدها**.
   *
   * والمدمّرة تُؤكَّد بكتابة الاسم في مكوّنٍ خاصّ، لا بنافذةٍ يضغطها الإبهام
   * بالعادة. وهذه هنا لأن الشاشة لا تنادي عالمياً — والمنصّة هي الطريق الوحيد.
   */
  confirm(question: string): boolean {
    if (!this.available) return false;
    return globalThis.confirm(question);
  },

  newId(): string {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  },

  /** تفضيل النظام للوضع الليلي — افتراضه النهاري حين لا يُعرَف */
  preferredColorScheme(): ColorSchemePreference {
    const mm = globalThis.matchMedia;
    if (typeof mm !== "function") return "light";
    try {
      return mm.call(globalThis, "(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch {
      return "light";
    }
  },

  onColorSchemeChange(listener: (scheme: ColorSchemePreference) => void): () => void {
    const mm = globalThis.matchMedia;
    if (typeof mm !== "function") return () => {};
    try {
      const query = mm.call(globalThis, "(prefers-color-scheme: dark)");
      const handler = (event: MediaQueryListEvent): void => {
        listener(event.matches ? "dark" : "light");
      };
      query.addEventListener("change", handler);
      return () => {
        query.removeEventListener("change", handler);
      };
    } catch {
      return () => {};
    }
  },

  /**
   * **كلُّ عطبٍ في المتصفّح لا يمسكه أحد — يُسمَع هنا.**
   *
   * فوعدٌ مرفوضٌ بلا `catch` يترك الشاشة **هيكلَ تحميلٍ إلى الأبد**، وخطأٌ في
   * سكربتٍ يترك بياضاً. وكلاهما «فشلٌ صامت» بنصّ المبدأ المركزيّ: لا رسالةَ
   * ولا كودَ يقوله المستخدم. وهذا هو الحدث الذي يجعلهما مسموعين.
   *
   * ويُمرَّر السببُ كما هو — **والمستقبِلُ هو من يحوّله إلى خطأٍ بكوده**، فلا
   * تُبنى الرسائلُ هنا.
   */
  onUnhandledFault(listener: (cause: unknown) => void): () => void {
    const target = globalThis.addEventListener as typeof globalThis.addEventListener | undefined;
    if (typeof target !== "function") return () => {};

    const onRejection = (event: PromiseRejectionEvent): void => {
      listener(event.reason);
    };
    const onError = (event: ErrorEvent): void => {
      listener(event.error ?? event.message);
    };
    globalThis.addEventListener("unhandledrejection", onRejection);
    globalThis.addEventListener("error", onError);
    return () => {
      globalThis.removeEventListener("unhandledrejection", onRejection);
      globalThis.removeEventListener("error", onError);
    };
  },

  /**
   * **Escape يقفل النافذةَ اللي فوق — كلَّ نافذةٍ، من موضعٍ واحد.**
   *
   * وكانت ولا نافذةٌ ولا لوحةٌ في النظام تُقفَل بـEscape: أربعون موضعاً، **ولا
   * واحدٌ كتبه** — فلوحةُ عنصر الخطة تبقى مفتوحةً والمستخدمُ يضغط ويضغط. ولو كُتب
   * في كلّ نافذةٍ لنُسي في الحادية والأربعين.
   *
   * فالنافذةُ تُعلن زرَّ قفلها بـ`data-close` — **الزرُّ نفسُه الذي يُضغَط بالفأرة**،
   * فيجري القفلُ بمساره هو (ما يُحفَظ قبله وما يُسأل عنه). وتُقفَل **أعمقُها**:
   * آخرُ نافذةٍ في ترتيب الصفحة هي التي فوق، فنافذةُ تأكيدٍ داخل لوحةٍ تُقفَل قبلها.
   *
   * **وما أمسك Escape قبلها لا يُقفلها** — قائمةٌ منسدلة أو منتقي إيموجي يعلّم
   * الحدثَ `preventDefault`، فيقفل نفسه وحده.
   */
  onEscapeCloseDialog(): () => void {
    const doc = globalThis.document as Document | undefined;
    if (!doc || typeof doc.addEventListener !== "function") return () => {};
    const handler = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const dialogs = doc.querySelectorAll('[role="dialog"]');
      const top = dialogs.item(dialogs.length - 1) as Element | null;
      const close = top?.querySelector<HTMLElement>("[data-close]") ?? null;
      if (close === null) return;
      event.preventDefault();
      close.click();
    };
    doc.addEventListener("keydown", handler);
    return () => {
      doc.removeEventListener("keydown", handler);
    };
  },

  /**
   * **لونُ شريط المتصفّح** — يتبع لونَ المشتري كما يتبعه كلُّ شيءٍ آخر.
   *
   * وكان مكتوباً في `index.html` بالإيد، **فيبقى تركوازياً** مهما لوّن المشتري
   * نظامَه: شريطُ المتصفّح على الهاتف، وشريطُ التطبيق بعد التثبيت.
   */
  setThemeColor(hex: string): void {
    const meta = globalThis.document?.querySelector('meta[name="theme-color"]');
    meta?.setAttribute("content", hex);
  },

  /**
   * **واسمُ التبويب اسمُ النظام** — وكان `Planner` مكتوباً بالإيد في `index.html`.
   *
   * **فمشترٍ سمّى نظامَه باسمه يرى اسمَنا في تبويب متصفّحه**، وفي الإشارات
   * المرجعية، وفي اسم التطبيق بعد التثبيت على الهاتف. وهو نفسُ عطب لون الهوية
   * بعينه: **قيمةٌ يملكها المشتري مكتوبةٌ في ملفٍّ يُبنى**.
   */
  setDocumentTitle(name: string): void {
    const document = globalThis.document;
    if (document && name.trim() !== "") document.title = name;
  },

  /** إعادةُ تحميل الصفحة — المخرجُ الوحيد بعد سقوط الشجرة */
  reload(): void {
    globalThis.location?.reload();
  },

  /**
   * **سجلُّ العطب — سببُه كاملاً تحت مرجعه.**
   *
   * فالرسالةُ على الشاشة تحمل المرجع، وهذا ما يُوجَد به الأثر. وهو الموضعُ
   * الوحيد الذي يلمس `console`، كأيّ لمسةٍ للمتصفّح.
   */
  logFault(reference: string, cause: unknown): void {
    globalThis.console?.error(reference, cause);
  },

  /** **عنوانُ البلاغ** — يضبطه تحميلُ الإعداد، فلا يعرف هذا الملفُّ عن السيرفر شيئاً */
  setFaultEndpoint(url: string): void {
    faultEndpoint = url;
  },

  /**
   * **العطلُ يصل السيرفر** — وكان يقف عند سجلّ جهاز الموظف، فلا نعرفه إلا إذا قاله صاحبُه.
   *
   * **ولا يُرفع بعطل**: طلبٌ لا ينتظره أحد، وفشلُه يُترك. **والمكانُ بلا معرّفات**: فالرابطُ قد يحمل
   * معرّفَ عميل، وما يخرج هو اسمُ الشاشة.
   */
  reportFault(
    kind: "crash" | "refused",
    reference: string,
    message: string,
    stack: string | undefined,
    where: string,
  ): void {
    if (faultEndpoint === null) return;
    void this.fetch(faultEndpoint, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", "idempotency-key": this.newId() },
      body: JSON.stringify({ kind, ref: reference, message, stack, where }),
    }).catch(() => undefined);
  },

  /** **اسمُ الشاشة بلا معرّفاتها** */
  faultLocation(): string {
    const path = typeof globalThis.location === "undefined" ? "" : globalThis.location.pathname;
    return path.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id");
  },

  /** تفضيلٌ صغير لهذا الجهاز وحده — لا بيانات عمل هنا أبداً */
  readPreference(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  /**
   * يكتب تفضيلاً، **ويقول هل نجح**.
   *
   * التخزين ممنوع في وضع التصفّح الخاص، وتفضيلٌ ضائع لا يوقف النظام. لكن
   * ابتلاع النتيجة بصمت هو العادة التي نبني ضدّها — فتخرج النتيجة، ومن أراد
   * أن يخبر المستخدم أن جهازه لن يتذكّر اختياره وجدها هنا.
   */
  writePreference(key: string, value: string): boolean {
    try {
      globalThis.localStorage?.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * **هل طلب صاحبُ الجهاز تقليلَ الحركة؟** — فالعدُّ والظهورُ يتوقّفان له.
   *
   * وبلا `matchMedia` (اختبارٌ أو متصفّحٌ قديم) **لا حركة**: الافتراضُ الآمن أن يرى
   * الرقمَ مباشرةً، لا أن يرى عدّاً لم يطلبه.
   */
  prefersReducedMotion(): boolean {
    const mm = globalThis.matchMedia;
    if (typeof mm !== "function") return true;
    try {
      return mm.call(globalThis, "(prefers-reduced-motion: reduce)").matches;
    } catch {
      return true;
    }
  },

  /** عرض النافذة — القوقعة تسأله مرة، ولا تسأله كل شاشة */
  isNarrow(): boolean {
    const mm = globalThis.matchMedia;
    if (typeof mm !== "function") return false;
    try {
      return mm.call(globalThis, "(max-width: 899px)").matches;
    } catch {
      return false;
    }
  },

  get pathname(): string {
    return globalThis.location?.pathname ?? "/";
  },

  /**
   * **يعيد العنوان إلى الجذر بلا إعادة تحميل** — بعد قراءة رابط الفتح.
   *
   * فالتنقّلُ داخل التطبيق لا يكتب في العنوان، فلو بقي الرابطُ لفتح التحديثُ الإشعارَ القديم.
   */
  dropPath(): boolean {
    if (!this.available) return false;
    try {
      globalThis.history.replaceState(null, "", "/");
      return true;
    } catch {
      return false;
    }
  },

  /**
   * **قيمةٌ في عنوان الصفحة** — ومن هنا وحده، فالمتصفّحُ خلف هذا الملفّ.
   *
   * ويحتاجها رابطُ الدعوة: من يفتحه لا جلسةَ له ولا قائمة، **والرمزُ في
   * العنوان هو كلُّ ما معه**.
   */
  queryValue(name: string): string | null {
    if (!this.available) return null;
    try {
      return new URL(globalThis.location.href).searchParams.get(name);
    } catch {
      return null;
    }
  },

  /**
   * **يمسح قيمةً من العنوان بلا إعادة تحميل.**
   *
   * فرمزُ الدعوة يبقى في شريط العنوان بعد استعماله — **ويُنسَخ مع الرابط**،
   * ويبقى في تاريخ المتصفّح وفي أي لقطة شاشة. ويُمسَح فورَ قراءته.
   *
   * **ويقول هل نجح** — كما تفعل `writePreference`. ولا يُبتلَع الفشلُ في
   * `catch` فارغ: قاعدةُ lint تمنعه، والقاعدةُ صحيحة.
   */
  dropQueryValue(name: string): boolean {
    if (!this.available) return false;
    try {
      const url = new URL(globalThis.location.href);
      if (!url.searchParams.has(name)) return true;
      url.searchParams.delete(name);
      globalThis.history.replaceState(null, "", url.toString());
      return true;
    } catch {
      return false;
    }
  },

  /** نسخٌ إلى الحافظة — **ويقول هل نجح**، فالصمتُ عند الفشل يُضيّع الرابط */
  async copyText(text: string): Promise<boolean> {
    if (!this.available) return false;
    try {
      await globalThis.navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },

  /** أصلُ الموقع — يُبنى منه رابطُ الدعوة الكامل */
  get origin(): string {
    return globalThis.location?.origin ?? "";
  },

  // ═══════════════════ تنبيهُ الجهاز — ومن هنا وحده ═══════════════════
  /**
   * **ويبقى المتصفّحُ خلف هذا الملفّ** — فـ`navigator` و`Notification`
   * و`atob` كلُّها هنا. وقاعدةُ lint تكسر البناء على ذكرها في غيره.
   *
   * وثلاثُ حالاتٍ لا حالتان: **مدعوم ومسموح**، **مدعوم ومحظور**، **غيرُ
   * مدعوم**. فالفرقُ بين الأخيرتين رسالةٌ مختلفة: المحظورُ يُفتَح من إعدادات
   * الموقع، وغيرُ المدعوم لا يُفتَح بشيء. وخلطُهما يجعل المستخدم يبحث في
   * إعداداتٍ لا تحلّ شيئاً.
   */
  get pushSupported(): boolean {
    if (nativePush() !== null) return true;
    return (
      this.available &&
      "serviceWorker" in globalThis.navigator &&
      typeof globalThis.PushManager === "function" &&
      typeof globalThis.Notification === "function"
    );
  },

  /**
   * **وداخل تطبيق أندرويد لا «محظور» يُقرأ مسبقاً** — فإذنُ النظام يُسأل عند الضغط،
   * ورفضُه يعود من `subscribePush` بسببه فيُقال.
   */
  get pushBlocked(): boolean {
    if (nativePush() !== null) return false;
    return this.pushSupported && globalThis.Notification.permission === "denied";
  },

  /**
   * **تطبيقُ أندرويد يُجهَّز عند الإقلاع** — قناةُ التنبيه باسم النظام، والنقرُ يفتح رابطَه.
   *
   * فتنبيهُ Firebase يصل بلا متصفّح، **والنقرُ عليه يفتح التطبيقَ على الرئيسية** ما لم
   * يُقرأ رابطُه — وهو «رابطٌ يفتح قائمة» بثوبٍ آخر. وفي المتصفّح لا يفعل شيئاً:
   * عاملُ الخدمة يفتح رابطَه بنفسه.
   */
  prepareNativePush(channelName: string): void {
    const native = nativePush();
    if (native === null) return;
    void native
      .createChannel({ id: "minwal", name: channelName, importance: 5, visibility: 1 })
      .catch((cause: unknown) => {
        this.logFault("native-push-channel", cause);
      });
    void native.addListener("pushNotificationActionPerformed", (action) => {
      const link = action.notification.data?.["link"];
      if (typeof link === "string" && link.startsWith("/")) globalThis.location.assign(link);
    });
  },

  /**
   * **يصغّر صورةً قبل رفعها** — أطولُ ضلعٍ `maxSide`، وJPEG على أرضيةٍ بيضاء.
   *
   * فصورةُ الهاتف أربعةُ ميجا وأكثر، **والوجهُ يُعرَض في دائرةٍ بثمانين نقطة**:
   * رفعُ الأصل يملأ خطَّ صاحبه ويُرفَض عند الحدّ. **ولا يرمي**: متصفّحٌ لا يفكّ
   * النوعَ يُعيد `null` فيُرفَع الأصلُ، **والسيرفرُ يقول سببَ رفضه بكوده**.
   */
  async shrinkImage(
    file: Blob,
    maxSide: number,
  ): Promise<{ readonly bytes: ArrayBuffer; readonly contentType: string } | null> {
    try {
      const bitmap = await globalThis.createImageBitmap(file);
      const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = new globalThis.OffscreenCanvas(width, height);
      const context = canvas.getContext("2d");
      if (!context) return null;
      /** **والشفافُ يصير أبيضَ لا أسود** — فـJPEG بلا قناةٍ شفّافة */
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.86 });
      return { bytes: await blob.arrayBuffer(), contentType: "image/jpeg" };
    } catch {
      return null;
    }
  },

  /** اسمُ هذا الجهاز كما يقرؤه صاحبُه — **لا سلسلةَ وكيلٍ خامة** */
  deviceLabel(): string {
    /** **والتطبيقُ يُسمّى تطبيقاً** — فصاحبُه يميّزه عن كروم على الهاتف نفسه */
    if (nativePush() !== null) return "Android app";
    const agent = globalThis.navigator?.userAgent ?? "";
    if (/iphone|ipad|ipod/i.test(agent)) return "iPhone / iPad";
    if (/android/i.test(agent)) return "Android";
    if (/mac os/i.test(agent)) return "Mac";
    if (/windows/i.test(agent)) return "Windows";
    return "جهاز";
  },

  /** هل الجهازُ مقطوعٌ عن الشبكة الآن؟ — **تُقرأ عند الرسم الأوّل** */
  isOffline(): boolean {
    return this.available && globalThis.navigator?.onLine === false;
  },

  /**
   * **ويُبلِّغ عند إخفاء الشاشة وعودتها** — ومنه تُشتقّ حالةُ «بعيد».
   *
   * **والخمولُ لا يُرى من السيرفر**: مقبسٌ مفتوحٌ لا يقول إن صاحبَه أمام
   * الشاشة. فالجهازُ يقول «شاشتي مخفيّة»، **والسيرفرُ يملك الخريطةَ والتوزيع**
   * — ولا يَقبل من أحدٍ إعلاناً عن حالة غيره.
   *
   * **ويمرّ من هنا كبقيّة عوامّ المتصفّح**: فقاعدةُ المستودع أن لا عامٌّ
   * يُنادى مباشرةً خارج هذا الموديول — وبها يصير الاختبارُ ممكناً، ويعمل
   * التنصيبُ على بيئةٍ بلا نافذة.
   */
  onVisibilityChange(listener: (hidden: boolean) => void): () => void {
    const doc = globalThis.document as Document | undefined;
    if (!doc || typeof doc.addEventListener !== "function") return () => {};
    const handler = (): void => {
      listener(doc.visibilityState === "hidden");
    };
    doc.addEventListener("visibilitychange", handler);
    return () => {
      doc.removeEventListener("visibilitychange", handler);
    };
  },

  /**
   * **يُبلِّغ عند انقطاع الشبكة وعودتها.**
   *
   * ومؤشّرُ الانقطاع من أساسيّات تطبيقٍ مثبَّت يُفتَح في الشارع: **من يرى شاشةً
   * لا تستجيب يظنّ العطبَ فينا**، فيعيد المحاولة ويفقد ما كتب.
   */
  onConnectivityChange(listener: (offline: boolean) => void): () => void {
    if (!this.available || typeof globalThis.addEventListener !== "function") return () => {};
    const online = (): void => {
      listener(false);
    };
    const offline = (): void => {
      listener(true);
    };
    globalThis.addEventListener("online", online);
    globalThis.addEventListener("offline", offline);
    return () => {
      globalThis.removeEventListener("online", online);
      globalThis.removeEventListener("offline", offline);
    };
  },

  /**
   * **يُمسك عرضَ التثبيت ويحتفظ به** — فالمتصفّحُ يعرضه مرّةً ثم يذهب.
   *
   * ولا يُعرَض الزرُّ قبل أن يصل هذا الحدث: **زرٌّ يُضغَط ولا يحدث شيء أسوأ من
   * زرٍّ غائب**. ومن ثبّت التطبيق لا يصله الحدثُ أصلاً، فلا يرى الزرّ.
   */
  onInstallAvailable(listener: (available: boolean) => void): () => void {
    if (!this.available || typeof globalThis.addEventListener !== "function") return () => {};
    const captured = (event: Event): void => {
      event.preventDefault();
      installPrompt = event as InstallPromptEvent;
      listener(true);
    };
    const installed = (): void => {
      installPrompt = null;
      listener(false);
    };
    globalThis.addEventListener("beforeinstallprompt", captured);
    globalThis.addEventListener("appinstalled", installed);
    if (installPrompt !== null) listener(true);
    return () => {
      globalThis.removeEventListener("beforeinstallprompt", captured);
      globalThis.removeEventListener("appinstalled", installed);
    };
  },

  /** يعرض نافذةَ التثبيت — **والقرارُ للمستخدم، ونتيجتُه لا تُسجَّل علينا** */
  async promptInstall(): Promise<void> {
    const prompt = installPrompt;
    if (!prompt) return;
    installPrompt = null;
    try {
      await prompt.prompt();
    } catch (cause) {
      this.logFault("install-prompt", cause);
    }
  },

  /**
   * **يسجّل عاملَ الخدمة عند الإقلاع** — فالتثبيتُ لا يتبع التنبيهات.
   *
   * وكان يُسجَّل **داخل الاشتراك في التنبيه وحده**: فمن لم يأذن بالتنبيهات لا
   * عاملَ خدمةٍ له، **والمتصفّحُ لا يعرض «أثبّت التطبيق» بلا عامل خدمة**. فكان
   * التثبيتُ — معيارُ قبول المرحلة السادسة — معلَّقاً على ميزةٍ أخرى بلا أن
   * يقول ذلك أحد.
   *
   * **ولا يسقط الإقلاعُ بفشله**: متصفّحٌ قديم أو سياقٌ غيرُ آمن يعني «لا تثبيت»،
   * لا شاشةً بيضاء.
   */
  registerWorker(): void {
    if (!this.available) return;
    const worker = globalThis.navigator?.serviceWorker;
    if (!worker) return;
    void worker.register("/sw.js").catch((cause: unknown) => {
      this.logFault("service-worker-registration", cause);
    });
  },

  /**
   * يطلب الإذن ويُثبِّت الاشتراك — **ويُعيد ما يُرسَل إلى السيرفر**.
   *
   * ولا يرمي: كلُّ فشلٍ يُعاد كـ`null` مع سببه، فتعرف الشاشةُ أيَّ رسالةٍ تقول.
   */
  async subscribePush(publicKey: string): Promise<
    | {
        readonly ok: true;
        readonly transport: "webpush";
        readonly endpoint: string;
        readonly p256dh: string;
        readonly auth: string;
      }
    | { readonly ok: true; readonly transport: "fcm"; readonly endpoint: string }
    | { readonly ok: false; readonly reason: "unsupported" | "blocked" | "failed" }
  > {
    if (!this.pushSupported) return { ok: false, reason: "unsupported" };
    /**
     * **وداخل تطبيق أندرويد: إذنُ النظام ورمزُ Firebase** — لا متصفّحَ في الطريق.
     *
     * ورمزُ الجهاز يُحفَظ هنا، **فبه يُعرَف «هذا الجهاز» في القائمة** كما يُعرَف
     * عنوانُ الويب من متصفّحه.
     */
    const native = nativePush();
    if (native !== null) {
      try {
        const permission = await native.requestPermissions();
        if (permission.receive !== "granted") return { ok: false, reason: "blocked" };
        const token = await new Promise<string>((resolve, reject) => {
          void native.addListener("registration", (got) => {
            resolve(got.value);
          });
          void native.addListener("registrationError", (error) => {
            reject(new Error(error.error));
          });
          void native.register().catch(reject);
        });
        this.writePreference(NATIVE_TOKEN, token);
        return { ok: true, transport: "fcm", endpoint: token };
      } catch (cause) {
        this.logFault("native-push-register", cause);
        return { ok: false, reason: "failed" };
      }
    }
    try {
      const permission = await globalThis.Notification.requestPermission();
      if (permission !== "granted") return { ok: false, reason: "blocked" };

      const registration = await globalThis.navigator.serviceWorker.register("/sw.js");
      await globalThis.navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = subscription.toJSON();
      const p256dh = json.keys?.["p256dh"];
      const auth = json.keys?.["auth"];
      if (!json.endpoint || !p256dh || !auth) return { ok: false, reason: "failed" };
      return { ok: true, transport: "webpush", endpoint: json.endpoint, p256dh, auth };
    } catch {
      return { ok: false, reason: "failed" };
    }
  },

  /** يُلغي اشتراكَ هذا الجهاز ويُعيد عنوانه — فالسيرفر يحذف صفَّه بعنوانه */
  async unsubscribePush(): Promise<string | null> {
    if (!this.pushSupported) return null;
    const native = nativePush();
    if (native !== null) {
      const token = this.readPreference(NATIVE_TOKEN);
      await native.unregister().catch((cause: unknown) => {
        this.logFault("native-push-unregister", cause);
      });
      this.writePreference(NATIVE_TOKEN, "");
      return token === "" ? null : token;
    }
    try {
      const registration = await globalThis.navigator.serviceWorker.getRegistration("/sw.js");
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return null;
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      return endpoint;
    } catch {
      return null;
    }
  },

  /** عنوانُ اشتراك هذا الجهاز إن كان مُثبَّتاً — تُعرَف به «أجهزتي» */
  async pushEndpoint(): Promise<string | null> {
    if (!this.pushSupported) return null;
    if (nativePush() !== null) {
      const token = this.readPreference(NATIVE_TOKEN);
      return token === null || token === "" ? null : token;
    }
    try {
      const registration = await globalThis.navigator.serviceWorker.getRegistration("/sw.js");
      const subscription = await registration?.pushManager.getSubscription();
      return subscription?.endpoint ?? null;
    } catch {
      return null;
    }
  },
} as const;

/**
 * مفتاحُ VAPID من نصّه إلى بايتاته — **والمعيارُ يطلبه كذلك**.
 *
 * ويُكتب هنا لأنه يلمس `atob`، ولا يُكتب في شاشة.
 */
function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = globalThis.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let at = 0; at < raw.length; at += 1) bytes[at] = raw.charCodeAt(at);
  return bytes;
}

/**
 * **جسرُ تطبيق أندرويد — يحقنه الغلافُ في الصفحة، ولا مكتبةَ في الويب.**
 *
 * فالشاشاتُ تُحمَّل من السيرفر داخل التطبيق، وGlobal `Capacitor` يصل بإضافاته
 * الأصلية. **وفي المتصفّح لا يوجد** — فيبقى كلُّ شيءٍ على تنبيه الويب كما كان.
 */
type NativePush = {
  requestPermissions(): Promise<{ readonly receive: string }>;
  register(): Promise<void>;
  unregister(): Promise<void>;
  createChannel(channel: {
    readonly id: string;
    readonly name: string;
    readonly importance: number;
    readonly visibility: number;
  }): Promise<void>;
  addListener(
    event: "registration",
    listener: (token: { readonly value: string }) => void,
  ): unknown;
  addListener(
    event: "registrationError",
    listener: (error: { readonly error: string }) => void,
  ): unknown;
  addListener(
    event: "pushNotificationActionPerformed",
    listener: (action: {
      readonly notification: { readonly data?: Readonly<Record<string, unknown>> };
    }) => void,
  ): unknown;
};

const NATIVE_TOKEN = "push.nativeToken";

function nativePush(): NativePush | null {
  const bridge = (
    globalThis as {
      Capacitor?: {
        isNativePlatform?: () => boolean;
        Plugins?: { PushNotifications?: NativePush };
      };
    }
  ).Capacitor;
  if (bridge?.isNativePlatform?.() !== true) return null;
  return bridge.Plugins?.PushNotifications ?? null;
}
